import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import {
  t,
  getUTCTimeStamp,
  triggerTrackingUrl,
  getVatPriceProps,
} from '@insight/toolkit-utils';
import Button from '../Button/Button';
import Currency from '../Currency/Currency';
import Loading from '../Loading/Loading';
import PartNumbers from '../PartNumbers/PartNumbers';
import Message from '../Message/Message';
import { Icon } from '../Icon';
import PDPImage from './PDPImage';
import Tooltip from '../Tooltip/Tooltip';
import { CONTRACT_TIMED_OUT_MESSAGE } from '../AddItemToCartModal/constants';

export const ProductDetailTabResource = ({
  addToCart,
  cesSpecs,
  setMiniPDP,
  materialId,
  partNumber,
  shortDescription,
  longDescription,
  insightPrice,
  priceInclVat,
  currency,
  icon,
  disableNavLinks,
  image,
  showPrice,
  categoryType,
  clickTrackingURL,
  showVAT,
  asyncPrice,
  richRelevanceSSEComplete,
  isIPSUserWithContract,
}) => {
  const asyncItem = asyncPrice ? asyncPrice[materialId] : null;
  const finalPrice = asyncItem ? asyncItem?.webPrice : insightPrice;
  const isContractTimedOut =
    richRelevanceSSEComplete && !asyncItem && isIPSUserWithContract;
  const isPriceAvailable = isIPSUserWithContract
    ? asyncItem
      ? !asyncItem?.callForPrice
      : false
    : true;
  const shouldShowPrice = showPrice && isPriceAvailable;

  const [addToCartState, setAddToCartState] = useState({
    loading: false,
    fulfilled: false,
    rejected: null,
  });
  const { exclVatProps, inclVatProps } = getVatPriceProps(showVAT);

  useEffect(() => {
    if (!addToCartState.fulfilled) return;
    // Set timer to remove added to cart message
    const timer = setTimeout(
      () => setAddToCartState({ loading: false, fulfilled: false }),
      10000
    );
    return () => clearTimeout(timer);
  }, [addToCartState.fulfilled]);

  const addToCartHandler = async () => {
    setAddToCartState({ loading: true, fulfilled: false });
    try {
      // RR tracking event
      triggerTrackingUrl(clickTrackingURL);
      // Add resource to cart
      await addToCart(
        [
          {
            clientBrowserDate: getUTCTimeStamp(),
            materialID: materialId,
            quantity: 1,
            contractID: asyncItem?.contractNumber,
          },
        ],
        categoryType
      );
      setAddToCartState({ loading: false, fulfilled: true });
    } catch (err) {
      setAddToCartState({
        loading: false,
        fulfilled: false,
        rejected: err.message,
      });
    }
  };

  const renderDescription = () => {
    if (disableNavLinks) return <span>{shortDescription}</span>
    return (
      <Button
        color="inline-link"
        size="small"
        onClick={() => setMiniPDP(materialId)}
      >
        {shortDescription}
      </Button>
    );
  };

  const renderSubTitle = () => {
    // Check if ces spec contains a pipe character
    if (cesSpecs && cesSpecs.includes('|')) {
      const cesSpecsArray = cesSpecs.split(/\|/g);
      if (!cesSpecsArray || !cesSpecsArray.length) return null;
      const bullets = cesSpecsArray.map((bullet, index) => {
        const showDivider = index !== cesSpecsArray.length - 1;
        return (
          <span key={index}>
            {bullet}
            {showDivider ? <span> • </span> : null}
          </span>
        );
      });
      return <div>{bullets}</div>
    }
    if (cesSpecs) return cesSpecs;
    return longDescription;
  };

  const renderAddingToChart = () => {
    if (addToCartState.loading) return <Loading />
    if (addToCartState.fulfilled) {
      return (
        <Message
          className="c-product-tabs__resource__confirmation__message"
          type="success"
        >
          {t('Added to cart')}
        </Message>
      );
    }
    return null;
  };
  return (
    <div className="c-product-tabs__resource o-grid">
      <div className="c-product-tabs__resource__thumbnail o-grid__item u-1/1 u-1/6@tablet">
        {icon ? (
          <Icon icon={icon} />
        ) : (
          <PDPImage src={image.url} alt={image.description} />
        )}
      </div>
      <div className="c-product-tabs__resource__content o-grid__item u-1/1 u-3/6@tablet o-grid">
        <div className="c-product-tabs__resource__title o-grid__item u-1/1">
          {renderDescription()}
        </div>
        <div className="c-product-tabs__resource__long-description o-grid__item u-1/1">
          {renderSubTitle()}
        </div>
        <div className="o-grid__item u-1/1">
          <PartNumbers inline insightPart={materialId} mfrPart={partNumber} />
        </div>
        {isIPSUserWithContract && (
          <div className="o-grid__item u-1/1">
            <div className="u-text-bold u-margin-bot-tiny c-product-tabs__resource__contract-name">
              {isContractTimedOut ? (
                <div>{t(CONTRACT_TIMED_OUT_MESSAGE)}</div>
              ) : (
                <Tooltip
                  isInModal
                  content={asyncItem?.abbreviation || asyncItem?.contractName}
                >
                  <span>
                    {asyncItem?.abbreviation || asyncItem?.contractName}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </div>
      <div
        className={cn(
          'c-product-tabs__resource__price o-grid__item u-3/6 u-1/6@tablet',
          { 'c-product-tabs__resource__price__vat': showVAT }
        )}
      >
        {shouldShowPrice && (
          <div
            className={cn('o-grid c-product-tabs__resource__price', {
              'c-product-tabs__resource__price__vat': showVAT,
            })}
          >
            <Currency
              value={finalPrice}
              currencyCode={currency}
              {...exclVatProps}
            />
            {showVAT && (
              <div className="c-add-item-to-cart__inc-vat-wrapper">
                <Currency
                  value={priceInclVat}
                  currencyCode={currency}
                  {...inclVatProps}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="c-product-tabs__resource__add-to-cart o-grid__item u-3/6 u-1/6@tablet">
        <Button
          color="secondary"
          size="small"
          isDisabled={addToCartState.loading || !isPriceAvailable}
          onClick={addToCartHandler}
        >
          {t('Add to cart')}
        </Button>
        <div className="c-product-tabs__resource__confirmation">
          {renderAddingToChart()}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailTabResource;
